import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import { toast } from "react-hot-toast";
import { apiScopes } from "../auth/msalConfig";
import { theme } from "../theme";

const API_BASE = import.meta.env.VITE_API_BASE_URL as string;

/** Deliberately minimal in-app label editor — the whole point is that an
 *  operator can fix a batch's boxes in ~two clicks per box (drag a rectangle,
 *  pick a label) without learning the underlying annotation service. Draw / relabel / delete only; no
 *  move/resize, no polygons, no attributes — delete and redraw instead. */

interface LabelDef { id: number; name: string; color: string | null }
interface Box { label_id: number; points: [number, number, number, number] }
interface Frame {
  index: number;
  name: string;
  width: number;
  height: number;
  boxes: Box[];
}
interface TaskData {
  task_id: number;
  batch_id: number | null;
  batch_name: string | null;
  completed: boolean;
  labels: LabelDef[];
  frames: Frame[];
  /** Frame indices excluded from the training-set export (reversible). */
  deleted_frames?: number[];
}

// Fallback palette for labels without a supplied colour. Literal hex rather
// than tokens because these are DATA colours drawn per label, not theme
// roles — but drawn from the brand's own status hues so a label never
// introduces a colour the rest of the interface never uses.
const FALLBACK_COLORS = ["#C7493B", "#3F9D5A", "#3E6E8E", "#E0A12E", "#7C5F25", "#4D7F73", "#A9603B", "#8C5A7A"];

// The central detector has ONE `person` class; "person with helmet" and
// "person without helmet" are a presentation layer the pipeline derives from the
// separate head/helmet models. Offering them here invited annotations the
// training set cannot express — the backend already folds them back to `person`
// (see labeler.LABEL_ALIASES), so the reviewer's careful distinction was
// silently discarded. Better not to offer a choice that does not survive.
const HELMET_VARIANTS = new Set(["person with helmet", "person without helmet"]);
const isTrainableLabel = (name: string) => !HELMET_VARIANTS.has(name.trim().toLowerCase());

function labelColor(labels: LabelDef[], id: number): string {
  const i = labels.findIndex((l) => l.id === id);
  return labels[i]?.color || FALLBACK_COLORS[(i + FALLBACK_COLORS.length) % FALLBACK_COLORS.length];
}

// Module scope rather than inside the component so the loading and error
// screens — which return before any of the editor state is usable — can be
// framed as the same card the rest of the page is built from, instead of
// dropping the reviewer onto bare text that reads like a crash.
const CARD: React.CSSProperties = {
  background: theme.bgCard, border: `1px solid ${theme.border}`,
  borderRadius: 6, padding: 14, display: "flex", flexDirection: "column", gap: 8,
};
const EYEBROW: React.CSSProperties = {
  margin: 0, fontSize: 10, fontWeight: 700, letterSpacing: "0.12em",
  textTransform: "uppercase", color: theme.textMuted,
};
/** Secondary/toggle button: outlined, never filled, so only the one primary
 *  action on the page carries the brand fill. */
const SECONDARY: React.CSSProperties = {
  background: "transparent", color: theme.textSecondary,
  border: `1px solid ${theme.border}`, borderRadius: 5,
  padding: "8px 14px", fontSize: theme.fontSizeXs, fontWeight: 600,
  fontFamily: theme.fontSans, cursor: "pointer",
};

export function ReviewPage() {
  const { taskId } = useParams<{ taskId: string }>();
  const navigate = useNavigate();
  // ?readonly=1 turns the editor into a viewer (reached from "View images" on
  // Model Updates): frames, boxes and the filmstrip render as usual, but every
  // way of changing anything — drawing, moving, deleting, relabeling,
  // excluding a frame, saving — is switched off.
  const [searchParams] = useSearchParams();
  const readonly = searchParams.get("readonly") === "1";
  const { instance, accounts } = useMsal();

  const [task, setTask] = useState<TaskData | null>(null);
  const [frames, setFrames] = useState<Frame[]>([]);
  const [imgUrls, setImgUrls] = useState<(string | null)[]>([]);
  const [cur, setCur] = useState(0);
  const [sel, setSel] = useState<number | null>(null);
  const [activeLabel, setActiveLabel] = useState<number | null>(null);
  const [dirty, setDirty] = useState(false);
  const [drawing, setDrawing] = useState<{ x1: number; y1: number; x2: number; y2: number } | null>(null);
  const [busy, setBusy] = useState<"save" | "complete" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  // Frames the reviewer dropped from the training set. The review service keeps the image but
  // leaves it out of the dataset export, so this is reversible — hence a toggle
  // rather than a destructive delete.
  const [excluded, setExcluded] = useState<Set<number>>(new Set());

  const svgRef = useRef<SVGSVGElement | null>(null);
  const drawStart = useRef<{ x: number; y: number } | null>(null);

  const fetchToken = useCallback(async () => {
    const r = await instance.acquireTokenSilent({ scopes: apiScopes, account: accounts[0] });
    return r.accessToken;
  }, [instance, accounts]);

  // ── Load task + frame images ────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    const urls: string[] = [];
    (async () => {
      try {
        const token = await fetchToken();
        const resp = await fetch(`${API_BASE}/api/annotate/tasks/${taskId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (resp.status === 404) {
          // The server explains itself here ("predates the review store" vs.
          // "not found"); pass that on rather than a bare number.
          const detail = await resp.json().then((j: { detail?: string }) => j.detail).catch(() => null);
          throw new Error(detail ?? "this review does not exist.");
        }
        if (!resp.ok) throw new Error(`the server answered with error ${resp.status}. Try again; if it keeps failing, contact an administrator.`);
        const data: TaskData = await resp.json();
        if (cancelled) return;
        setTask(data);
        setFrames(data.frames);
        setExcluded(new Set(data.deleted_frames ?? []));
        setActiveLabel(data.labels[0]?.id ?? null);
        setImgUrls(new Array(data.frames.length).fill(null));
        setLoading(false);
        // Fetch images sequentially — a batch is a handful of frames, and
        // <img src> can't send the Bearer header, hence fetch → blob URLs.
        for (const f of data.frames) {
          const ir = await fetch(`${API_BASE}/api/annotate/tasks/${taskId}/frames/${f.index}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (!ir.ok) continue;
          const url = URL.createObjectURL(await ir.blob());
          urls.push(url);
          if (cancelled) return;
          setImgUrls((prev) => {
            const next = [...prev];
            next[f.index] = url;
            return next;
          });
        }
      } catch (err) {
        if (!cancelled) {
          // Error.message, not String(err): "Error: TypeError: Failed to fetch"
          // is developer-speak. A network failure gets its own plain sentence.
          const msg = err instanceof TypeError
            ? "the server could not be reached. Check your connection and try again."
            : err instanceof Error ? err.message : String(err);
          setError(msg);
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
      urls.forEach((u) => URL.revokeObjectURL(u));
    };
  }, [taskId, fetchToken]);

  const frame = frames[cur];

  // ── Box editing (draw / move / resize) ──────────────────────────────────
  // One drag gesture at a time, tracked in a ref: drawing a new box (from
  // empty canvas), moving a box (grab anywhere inside it) or resizing one
  // (grab a corner handle of the selected box).
  type DragState =
    | { mode: "draw" }
    | { mode: "move"; idx: number; x: number; y: number; orig: Box["points"]; moved: boolean }
    | { mode: "resize"; idx: number; corner: 0 | 1 | 2 | 3; orig: Box["points"]; moved: boolean };
  const dragRef = useRef<DragState | null>(null);

  // Box points are stored in "fraction × full image width/height" units (see
  // the server-side review submission), but that fraction is itself
  // relative to the CENTRE-CROPPED SQUARE the labeling models actually see
  // (crop_side = min(width, height)) — the same convention DetectionCard.tsx
  // relies on for the Detections page. The canvas below renders the image
  // center-cropped to that same square (aspectRatio: 1, objectFit: cover) so
  // points-as-stored land in the right place with zero data transform. Mouse
  // coordinates have to make the same crop-window mapping in reverse.
  const toImageCoords = (e: React.MouseEvent): { x: number; y: number } | null => {
    const svg = svgRef.current;
    if (!svg || !frame) return null;
    const r = svg.getBoundingClientRect();
    const cropSide  = Math.min(frame.width, frame.height);
    const offsetX   = (frame.width - cropSide) / 2;
    const offsetY   = (frame.height - cropSide) / 2;
    return {
      x: Math.max(0, Math.min(frame.width, offsetX + ((e.clientX - r.left) / r.width) * cropSide)),
      y: Math.max(0, Math.min(frame.height, offsetY + ((e.clientY - r.top) / r.height) * cropSide)),
    };
  };

  const updateBox = (idx: number, points: Box["points"]) => {
    setFrames((prev) => prev.map((f, i) =>
      i === cur ? { ...f, boxes: f.boxes.map((b, bi) => (bi === idx ? { ...b, points } : b)) } : f
    ));
  };

  const onCanvasMouseDown = (e: React.MouseEvent) => {
    if (readonly || e.button !== 0) return;
    // preventDefault stops the browser starting a text/image selection drag —
    // without it, dragging a rectangle blue-highlights the surrounding page.
    e.preventDefault();
    const p = toImageCoords(e);
    if (!p) return;
    setSel(null);
    dragRef.current = { mode: "draw" };
    drawStart.current = p;
    setDrawing({ x1: p.x, y1: p.y, x2: p.x, y2: p.y });
  };

  const startMove = (e: React.MouseEvent, bi: number) => {
    if (readonly || e.button !== 0 || !frame) return;
    e.preventDefault();
    e.stopPropagation();
    const p = toImageCoords(e);
    if (!p) return;
    setSel(bi);
    dragRef.current = { mode: "move", idx: bi, x: p.x, y: p.y, orig: frame.boxes[bi].points, moved: false };
  };

  const startResize = (e: React.MouseEvent, bi: number, corner: 0 | 1 | 2 | 3) => {
    if (readonly || e.button !== 0 || !frame) return;
    e.preventDefault();
    e.stopPropagation();
    dragRef.current = { mode: "resize", idx: bi, corner, orig: frame.boxes[bi].points, moved: false };
  };

  const onCanvasMouseMove = (e: React.MouseEvent) => {
    const d = dragRef.current;
    if (!d || !frame) return;
    const p = toImageCoords(e);
    if (!p) return;

    if (d.mode === "draw") {
      if (drawStart.current)
        setDrawing({ x1: drawStart.current.x, y1: drawStart.current.y, x2: p.x, y2: p.y });
      return;
    }
    if (d.mode === "move") {
      const [ox1, oy1, ox2, oy2] = d.orig;
      // Clamp the shift so the whole box stays inside the image.
      const dx = Math.max(-ox1, Math.min(frame.width - ox2, p.x - d.x));
      const dy = Math.max(-oy1, Math.min(frame.height - oy2, p.y - d.y));
      if (dx !== 0 || dy !== 0) d.moved = true;
      updateBox(d.idx, [ox1 + dx, oy1 + dy, ox2 + dx, oy2 + dy]);
      return;
    }
    // resize: the grabbed corner follows the mouse; crossing the opposite
    // corner is allowed mid-drag and normalized on mouseup.
    const [ox1, oy1, ox2, oy2] = d.orig;
    const pts: Box["points"] =
      d.corner === 0 ? [p.x, p.y, ox2, oy2] :
      d.corner === 1 ? [ox1, p.y, p.x, oy2] :
      d.corner === 2 ? [ox1, oy1, p.x, p.y] :
                       [p.x, oy1, ox2, p.y];
    d.moved = true;
    updateBox(d.idx, pts);
  };

  const onCanvasMouseUp = () => {
    const d = dragRef.current;
    dragRef.current = null;

    if (!d || !frame) { drawStart.current = null; setDrawing(null); return; }

    if (d.mode === "draw") {
      const dr = drawing;
      drawStart.current = null;
      setDrawing(null);
      if (!dr || activeLabel == null) return;
      const x1 = Math.min(dr.x1, dr.x2), x2 = Math.max(dr.x1, dr.x2);
      const y1 = Math.min(dr.y1, dr.y2), y2 = Math.max(dr.y1, dr.y2);
      // Ignore accidental clicks — a real box is at least a few pixels.
      if (x2 - x1 < 8 || y2 - y1 < 8) return;
      setFrames((prev) => prev.map((f, i) =>
        i === cur ? { ...f, boxes: [...f.boxes, { label_id: activeLabel, points: [x1, y1, x2, y2] as Box["points"] }] } : f
      ));
      setSel(frame.boxes.length);   // the freshly appended box
      setDirty(true);
      return;
    }

    // move / resize finished: normalize (a crossed resize swaps corners).
    if (d.moved) {
      setFrames((prev) => prev.map((f, i) =>
        i === cur ? {
          ...f,
          boxes: f.boxes.map((b, bi) => {
            if (bi !== d.idx) return b;
            const [x1, y1, x2, y2] = b.points;
            return { ...b, points: [Math.min(x1, x2), Math.min(y1, y2),
                                    Math.max(x1, x2), Math.max(y1, y2)] as Box["points"] };
          }),
        } : f
      ));
      setDirty(true);
    }
  };

  const deleteSelected = useCallback(() => {
    if (readonly || sel == null) return;
    setFrames((prev) => prev.map((f, i) =>
      i === cur ? { ...f, boxes: f.boxes.filter((_, bi) => bi !== sel) } : f
    ));
    setSel(null);
    setDirty(true);
  }, [readonly, sel, cur]);

  /** Drop the current image from the training set (or put it back).
   *  Persisted immediately — it is a property of the review task, not part of
   *  the box edits that the Save button flushes. */
  const toggleExcluded = useCallback(async (idx: number) => {
    if (readonly) return;
    const next = new Set(excluded);
    const removing = !next.has(idx);
    if (removing) next.add(idx); else next.delete(idx);
    setExcluded(next);                       // optimistic
    try {
      const token = await fetchToken();
      const resp = await fetch(`${API_BASE}/api/annotate/tasks/${taskId}/deleted-frames`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ frames: [...next] }),
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      toast.success(removing ? "Image dropped from the training set" : "Image restored");
    } catch (err) {
      setExcluded(excluded);                 // roll back to the pre-toggle set
      toast.error(`Could not update: ${err}`);
    }
  }, [readonly, excluded, fetchToken, taskId]);

  const relabelSelected = (labelId: number) => {
    if (readonly) return;
    setActiveLabel(labelId);
    if (sel == null) return;
    setFrames((prev) => prev.map((f, i) =>
      i === cur ? { ...f, boxes: f.boxes.map((b, bi) => (bi === sel ? { ...b, label_id: labelId } : b)) } : f
    ));
    setDirty(true);
  };

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      // Don't hijack keys while the reviewer is typing in a field.
      const el = e.target as HTMLElement | null;
      if (el && (/^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName) || el.isContentEditable)) return;

      if (!readonly && (e.key === "Delete" || e.key === "Backspace")) {
        // One key, two scopes: with a box selected it removes that box (the
        // frequent action); with nothing selected it drops the whole image from
        // the training set. Reversible either way — boxes via Save, images via
        // toggling back.
        if (sel != null) deleteSelected();
        else if (frames.length) void toggleExcluded(cur);
      }
      if (e.key === "ArrowLeft") setCur((c) => Math.max(0, c - 1));
      if (e.key === "ArrowRight") setCur((c) => Math.min(frames.length - 1, c + 1));
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [readonly, deleteSelected, frames.length, sel, cur, toggleExcluded]);

  useEffect(() => { setSel(null); }, [cur]);

  // ── Save / complete ─────────────────────────────────────────────────────
  const save = useCallback(async (): Promise<boolean> => {
    try {
      const token = await fetchToken();
      const resp = await fetch(`${API_BASE}/api/annotate/tasks/${taskId}/annotations`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ frames: frames.map((f) => f.boxes) }),
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      setDirty(false);
      return true;
    } catch (err) {
      toast.error(`Save failed: ${err}`);
      return false;
    }
  }, [fetchToken, taskId, frames]);

  const handleSave = async () => {
    setBusy("save");
    if (await save()) toast.success("Labels saved.");
    setBusy(null);
  };

  // Land the operator on the batch this review belongs to, not on an empty
  // Model Updates page: the next action ("approve / start retraining") lives in
  // that batch's detail panel.
  const backToBatch = () =>
    navigate(task?.batch_id != null ? `/orchestrator?batch=${task.batch_id}` : "/orchestrator");

  const handleComplete = async () => {
    setBusy("complete");
    try {
      if (!(await save())) return;
      const token = await fetchToken();
      const resp = await fetch(`${API_BASE}/api/annotate/tasks/${taskId}/complete`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      toast.success("Review completed — ready for retraining approval.");
      backToBatch();
    } catch (err) {
      toast.error(`Completing the review failed: ${err}`);
    } finally {
      setBusy(null);
    }
  };

  // How wide the canvas column may be. The square canvas plus the scrubber and
  // its caption have to fit between the column's top and the bottom of the
  // window; anything wider than that spills and starts the page scrolling.
  const canvasColRef = useRef<HTMLDivElement>(null);
  const [canvasMax, setCanvasMax] = useState<number | null>(null);
  useLayoutEffect(() => {
    const measure = () => {
      const el = canvasColRef.current;
      if (!el) return;
      const top = el.getBoundingClientRect().top;
      if (top <= 0) return;
      const BELOW = 96; // scrubber row + counts line + page padding
      setCanvasMax(Math.max(320, window.innerHeight - top - BELOW));
    };
    measure();
    const raf = requestAnimationFrame(measure);
    const observer = new ResizeObserver(measure);
    if (canvasColRef.current?.parentElement) observer.observe(canvasColRef.current.parentElement);
    window.addEventListener("resize", measure);
    return () => {
      cancelAnimationFrame(raf);
      observer.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [loading]);

  // ── Render ──────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div style={{ padding: 24, boxSizing: "border-box" }}>
        <div style={CARD}>
          <p style={EYEBROW}>Label review</p>
          <span style={{ color: theme.textMuted, fontSize: theme.fontSizeXs }}>Loading review…</span>
        </div>
      </div>
    );
  }
  if (error || !task || !frame) {
    return (
      <div style={{ padding: 24, boxSizing: "border-box" }}>
        <div style={{ ...CARD, borderColor: theme.danger, background: theme.dangerSoft }}>
          <p style={{ ...EYEBROW, color: theme.danger }}>Label review unavailable</p>
          <span style={{ color: theme.textPrimary, fontSize: theme.fontSizeXs }}>
            Failed to load the review: {error ?? "no frames"}
          </span>
        </div>
      </div>
    );
  }

  const strokeW = Math.max(2, frame.width / 400);
  const fontSize = Math.max(13, frame.width / 55);
  const selBox = sel != null ? frame.boxes[sel] : undefined;
  const isExcluded = excluded.has(cur);

  return (
    <div style={{ padding: 24, boxSizing: "border-box", width: "100%" }}>
      {/* Header. This page has no entry in the nav rail — it is only ever
          reached from a link — so the heading is the only thing naming it. */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        gap: 12, flexWrap: "wrap", marginBottom: 14,
      }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap", minWidth: 0 }}>
          <h2 style={{ margin: 0, color: theme.textPrimary, fontSize: 18, fontWeight: 700 }}>Label review</h2>
          {task.batch_name && (
            <span style={{ color: theme.textMuted, fontSize: theme.fontSizeXs }}>{task.batch_name}</span>
          )}
          {readonly && (
            <span style={{
              color: theme.textSecondary, fontSize: 10, fontWeight: 700, letterSpacing: "0.1em",
              border: `1px solid ${theme.borderStrong}`, borderRadius: 3, padding: "2px 7px",
            }}>
              READ-ONLY
            </span>
          )}
          {task.completed && (
            <span style={{ color: theme.success, fontSize: 11, fontWeight: 700 }}>✓ already completed</span>
          )}
          {dirty && (
            <span style={{ color: theme.warning, fontSize: 11, fontWeight: 700 }}>● unsaved changes</span>
          )}
        </div>
        {readonly ? (
          <button onClick={backToBatch} style={SECONDARY}>
            ← Back to Model Updates
          </button>
        ) : (
        <div style={{ display: "flex", gap: 8 }}>
          <button
            onClick={handleSave}
            disabled={busy !== null || !dirty}
            style={{
              ...SECONDARY, fontWeight: 700,
              // Only a pending edit earns the accent outline — an idle Save
              // button that looks armed trains the reviewer to ignore it.
              color: dirty ? theme.goldText : theme.textMuted,
              borderColor: dirty ? theme.gold : theme.border,
              cursor: busy || !dirty ? "default" : "pointer",
            }}
          >
            {busy === "save" ? "Saving…" : "Save"}
          </button>
          {!task.completed && (
            <button
              onClick={handleComplete}
              disabled={busy !== null}
              className={busy ? undefined : "btn-pulse"}
              style={{
                background: theme.gold, color: theme.onGold, border: "none", borderRadius: 5,
                padding: "8px 14px", fontSize: theme.fontSizeXs, fontWeight: 700,
                fontFamily: theme.fontSans,
                cursor: busy ? "default" : "pointer", opacity: busy ? 0.6 : 1,
              }}
            >
              {busy === "complete" ? "Completing…" : "Mark review complete"}
            </button>
          )}
        </div>
        )}
      </div>

      {/* Canvas on the left, the editing controls stacked in a rail on the
          right. `flexWrap` rather than a media query: below roughly 700px of
          content width the rail simply drops under the canvas. */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 18, alignItems: "flex-start" }}>
        <div ref={canvasColRef} style={{
          flex: "8 1 380px", minWidth: 0,
          // The canvas is square, so its HEIGHT is what actually limits it —
          // sizing it by column width alone left a tall window mostly empty and
          // made a wide one overflow. Capped by the room left below the header,
          // measured rather than assumed, because the header wraps at narrow
          // widths and any constant would be wrong there.
          maxWidth: canvasMax ?? undefined,
        }}>

      {/* Canvas */}
      <div style={{
        position: "relative", background: theme.bgInput, userSelect: "none",
        border: `1px solid ${theme.border}`, borderRadius: 5, overflow: "hidden",
      }}>
        {imgUrls[cur] ? (
          // Center-cropped square, same as DetectionCard.tsx — see toImageCoords
          // above for why the box points line up with this specific crop.
          <img
            src={imgUrls[cur]!} alt={frame.name} draggable={false}
            style={{ display: "block", width: "100%", aspectRatio: "1", objectFit: "cover", objectPosition: "center" }}
          />
        ) : (
          <div style={{
            width: "100%", aspectRatio: "1", display: "flex", alignItems: "center",
            justifyContent: "center", color: theme.textMuted, fontSize: theme.fontSizeXs,
          }}>
            Loading image…
          </div>
        )}
        <svg
          ref={svgRef}
          viewBox={`0 0 ${frame.width} ${frame.height}`}
          preserveAspectRatio="xMidYMid slice"
          style={{ position: "absolute", inset: 0, width: "100%", height: "100%", cursor: readonly ? "default" : "crosshair" }}
          onMouseDown={onCanvasMouseDown}
          onMouseMove={onCanvasMouseMove}
          onMouseUp={onCanvasMouseUp}
          onMouseLeave={onCanvasMouseUp}
        >
          {frame.boxes.map((b, bi) => {
            const color = labelColor(task.labels, b.label_id);
            const name = task.labels.find((l) => l.id === b.label_id)?.name ?? "?";
            const [x1, y1, x2, y2] = b.points;
            const isSel = bi === sel;
            const hs = Math.max(5, strokeW * 2.5);   // corner-handle half-size
            const corners: [number, number, 0 | 1 | 2 | 3][] =
              [[x1, y1, 0], [x2, y1, 1], [x2, y2, 2], [x1, y2, 3]];
            // Shared box style: thin colored border, translucent fill of the
            // same colour, and a small caption tab on the TOP edge — the label
            // colour as the backing with light text on it, so the caption
            // itself carries the colour coding even over a busy image.
            const capH = fontSize * 1.35;
            const capW = name.length * fontSize * 0.62 + fontSize * 0.7;
            const capY = Math.max(0, y1 - capH);
            return (
              <g key={bi}
                 onMouseDown={(e) => startMove(e, bi)}
                 style={{ cursor: readonly ? "default" : "move" }}>
                <rect x={x1} y={y1} width={x2 - x1} height={y2 - y1} rx={2}
                      fill={color + (isSel ? "33" : "1a")}
                      stroke={color} strokeWidth={isSel ? strokeW * 1.8 : strokeW} />
                <rect x={x1} y={capY} width={capW} height={capH}
                      fill={color} rx={2} />
                <text x={x1 + fontSize * 0.35} y={capY + capH * 0.74}
                      fill={theme.onDark} fontSize={fontSize} fontWeight={700}
                      style={{ userSelect: "none", fontFamily: theme.fontSans }}>
                  {name}
                </text>
                {isSel && corners.map(([hx, hy, c]) => (
                  <rect key={c}
                        x={hx - hs} y={hy - hs} width={hs * 2} height={hs * 2}
                        fill={theme.onDark} stroke={color} strokeWidth={strokeW / 1.5}
                        style={{ cursor: c === 0 || c === 2 ? "nwse-resize" : "nesw-resize" }}
                        onMouseDown={(e) => startResize(e, bi, c)} />
                ))}
              </g>
            );
          })}
          {drawing && (
            <rect
              x={Math.min(drawing.x1, drawing.x2)} y={Math.min(drawing.y1, drawing.y2)}
              width={Math.abs(drawing.x2 - drawing.x1)} height={Math.abs(drawing.y2 - drawing.y1)}
              fill="none" stroke={activeLabel != null ? labelColor(task.labels, activeLabel) : theme.onDark}
              strokeWidth={strokeW} strokeDasharray={`${strokeW * 3} ${strokeW * 2}`}
            />
          )}
        </svg>

        {/* Overlay chips sit above the SVG but must not swallow the drag
            gestures that draw and move boxes — hence pointerEvents: none. */}
        <span style={{
          position: "absolute", top: 8, left: 10, pointerEvents: "none",
          background: "rgba(26,28,30,0.72)", color: theme.onDark,
          fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase",
          padding: "2px 8px", borderRadius: 3, fontFamily: theme.fontSans,
        }}>
          Frame {cur + 1} — {frame.name}
        </span>
        {isExcluded && (
          <div style={{
            position: "absolute", inset: 0, pointerEvents: "none",
            background: "rgba(26,28,30,0.45)",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <span style={{
              background: theme.bgCard, color: theme.textPrimary,
              border: `1px solid ${theme.borderStrong}`, borderRadius: 4,
              padding: "6px 14px", fontSize: theme.fontSizeXs, fontWeight: 700,
              letterSpacing: "0.08em", textTransform: "uppercase",
            }}>
              Excluded from training set
            </span>
          </div>
        )}
      </div>

      {/* Frame navigation: prev/next plus a scrubber for jumping across a long
          batch. Kept directly under the canvas so the eye never leaves the
          image it is stepping through. */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 12 }}>
        <button
          onClick={() => setCur((c) => Math.max(0, c - 1))}
          disabled={cur === 0}
          style={{
            ...SECONDARY, padding: "6px 12px",
            color: cur === 0 ? theme.textMuted : theme.textSecondary,
            cursor: cur === 0 ? "default" : "pointer",
          }}
        >
          ← Prev
        </button>
        <input
          type="range"
          min={0}
          max={Math.max(0, frames.length - 1)}
          value={cur}
          onChange={(e) => setCur(Number(e.target.value))}
          aria-label="Jump to image"
          title={`Image ${cur + 1} / ${frames.length}`}
          style={{ flex: 1, minWidth: 0, accentColor: theme.gold, cursor: "pointer" }}
        />
        <button
          onClick={() => setCur((c) => Math.min(frames.length - 1, c + 1))}
          disabled={cur >= frames.length - 1}
          style={{
            ...SECONDARY, padding: "6px 12px",
            color: cur >= frames.length - 1 ? theme.textMuted : theme.textSecondary,
            cursor: cur >= frames.length - 1 ? "default" : "pointer",
          }}
        >
          Next →
        </button>
      </div>

      <div style={{ marginTop: 8, color: theme.textSecondary, fontSize: theme.fontSizeXs }}>
        <span style={{ fontFamily: theme.fontMono }}>{cur + 1} / {frames.length}</span>
        <span style={{ color: theme.textMuted }}>
          {" "}· {frame.boxes.length} box{frame.boxes.length === 1 ? "" : "es"}
        </span>
        {isExcluded && (
          <span style={{ color: theme.danger, fontWeight: 700 }}> · excluded from training set</span>
        )}
      </div>

        </div>

        {/* Editing rail. Read-only keeps just a label legend and the frame
            shortcuts; the cards that exist to change something go away. */}
        {readonly ? (
        <div style={{ flex: "1 1 250px", minWidth: 0, display: "flex", flexDirection: "column", gap: 14 }}>
          <div style={CARD}>
            <p style={EYEBROW}>Labels</p>
            <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
              {task.labels.filter((l) => isTrainableLabel(l.name)).map((l) => (
                <div key={l.id} style={{
                  display: "flex", alignItems: "center", gap: 9, padding: "8px 10px",
                  borderRadius: 5, fontSize: theme.fontSizeXs, color: theme.textPrimary,
                  border: `1px solid ${theme.border}`,
                }}>
                  <span style={{ width: 11, height: 11, borderRadius: 3, background: labelColor(task.labels, l.id), flexShrink: 0 }} />
                  <span style={{ flex: 1, minWidth: 0 }}>{l.name}</span>
                </div>
              ))}
            </div>
          </div>
          <div style={CARD}>
            <p style={EYEBROW}>Keyboard</p>
            <span style={{ fontSize: 11, color: theme.textMuted, lineHeight: 1.6 }}>
              <b>←</b> / <b>→</b> switch image. Editing is disabled on this view.
            </span>
          </div>
        </div>
        ) : (
        <div style={{ flex: "1 1 250px", minWidth: 0, display: "flex", flexDirection: "column", gap: 14 }}>

          <div style={CARD}>
            <p style={EYEBROW}>Labels — draw a box, then pick</p>
            <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
              {task.labels.filter((l) => isTrainableLabel(l.name)).map((l) => {
                const color = labelColor(task.labels, l.id);
                const active = l.id === (selBox ? selBox.label_id : activeLabel);
                return (
                  <button
                    key={l.id}
                    onClick={() => relabelSelected(l.id)}
                    style={{
                      display: "flex", alignItems: "center", gap: 9, padding: "8px 10px",
                      borderRadius: 5, textAlign: "left", cursor: "pointer",
                      fontSize: theme.fontSizeXs, fontFamily: theme.fontSans, fontWeight: 500,
                      color: theme.textPrimary,
                      // The accent, not the label's own hue, marks the active
                      // row: the swatch already carries the label colour, and
                      // reusing it for selection would make the two unreadable.
                      border: `1px solid ${active ? theme.gold : theme.border}`,
                      background: active ? theme.goldSoft : "transparent",
                    }}
                  >
                    <span style={{ width: 11, height: 11, borderRadius: 3, background: color, flexShrink: 0 }} />
                    <span style={{ flex: 1, minWidth: 0 }}>{l.name}</span>
                    {active && (
                      <span style={{
                        fontSize: 10, color: theme.goldText, fontWeight: 700, letterSpacing: "0.08em",
                      }}>
                        ACTIVE
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          <div style={CARD}>
            <p style={EYEBROW}>Selected box</p>
            {selBox ? (
              <>
                <span style={{
                  fontSize: theme.fontSizeXs, fontWeight: 600,
                  color: labelColor(task.labels, selBox.label_id),
                }}>
                  {task.labels.find((l) => l.id === selBox.label_id)?.name ?? "?"}
                </span>
                <button
                  onClick={deleteSelected}
                  style={{
                    ...SECONDARY, width: "100%", fontWeight: 700,
                    color: theme.danger, borderColor: theme.danger,
                  }}
                >
                  Delete box
                </button>
                <span style={{ fontSize: 11, color: theme.textMuted }}>
                  Click a label above to relabel the selected box.
                </span>
              </>
            ) : (
              <span style={{ fontSize: theme.fontSizeXs, color: theme.textMuted }}>
                None — click a box to select it, or drag on the image to draw a new one. Drag a box to
                move it, drag its corners to resize.
              </span>
            )}
          </div>

          <div style={CARD}>
            <p style={EYEBROW}>This frame</p>
            <button
              onClick={() => void toggleExcluded(cur)}
              title="Drop this image from the training set (reversible)"
              // Neutral, not danger-red: excluding a frame is reversible and
              // routine, and the state it produces is already shouted by the
              // overlay across the image itself.
              style={{ ...SECONDARY, width: "100%", background: isExcluded ? theme.bgInput : "transparent" }}
            >
              {isExcluded ? "Restore to training set" : "Exclude from training set"}
            </button>
            <span style={{ fontSize: 11, color: theme.textMuted }}>
              Excluded frames stay in the batch but are left out of the training-set export —
              reversible.
            </span>
          </div>

          {/* The shortcuts are the fast path through a batch, so they stay
              on screen rather than hiding behind a help affordance. */}
          <div style={CARD}>
            <p style={EYEBROW}>Keyboard</p>
            <span style={{ fontSize: 11, color: theme.textMuted, lineHeight: 1.6 }}>
              <b>←</b> / <b>→</b> switch image · <b>Del</b> removes the selected box, or drops the
              image from the training set when nothing is selected.
            </span>
          </div>

        </div>
        )}
      </div>
    </div>
  );
}
